import React, { useMemo } from 'react';
import type { PracticeArea } from '../../types';
import { useData } from '../../context/DataContext';
import LocalizedLink from '../ui/LocalizedLink';
import { useTranslation } from 'react-i18next';

interface OtherPracticeAreasProps {
  currentSlug: string;
}

const OtherPracticeAreas: React.FC<OtherPracticeAreasProps> = ({ currentSlug }) => {
  const { t } = useTranslation('common');
  const { practiceAreas: allAreas } = useData();

  const otherAreas = useMemo(() => {
    const filtered = allAreas.filter(area => area.slug !== currentSlug);
    // Shuffle and take the first 4
    return filtered.sort(() => 0.5 - Math.random()).slice(0, 4);
  }, [currentSlug, allAreas]);

  if (otherAreas.length === 0) {
    return null;
  }

  return (
    <div className="py-24">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-12">{t('otherPracticeAreas.title')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {otherAreas.map(area => (
            <LocalizedLink
              key={area.slug}
              to={`/practice-areas/${area.slug}`}
              className="group block p-6 bg-white/5 border border-white/10 rounded-2xl text-center hover:bg-white/10 hover:border-white/20 transition-all duration-300 transform hover:-translate-y-1"
            >
              <h3 className="font-semibold text-lg text-brand-gold-light group-hover:text-white transition-colors">{area.title}</h3>
              <p className="text-sm text-white/70 mt-2 line-clamp-3">{area.subtitle}</p>
            </LocalizedLink>
          ))}
        </div>
        <div className="mt-12 text-center">
          <LocalizedLink
            to="/practice-areas"
            className="inline-block px-8 py-4 text-lg font-bold text-brand-dark bg-brand-gold rounded-full hover:bg-brand-gold-light transition-colors"
          >
            {t('otherPracticeAreas.viewAll')}
          </LocalizedLink>
        </div>
      </div>
    </div>
  );
};

export default OtherPracticeAreas;